import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
const KEY = 'kontor-theme';

const read = (): Theme => (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');

/** Gespeicherte Wahl, falls es eine gibt. Browser ohne Speicher (privates Fenster) liefern null, dann gilt die Vorgabe des Geräts. */
function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

/** Design setzen und merken. Das Skript theme-init.js liest die Wahl beim Laden, damit nichts aufblitzt. */
function apply(theme: Theme, remember: boolean) {
  document.documentElement.setAttribute('data-theme', theme);
  if (!remember) return;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // ohne Speicher gilt die Wahl nur bis zum Neuladen
  }
}

/**
 * Umschalter zwischen hellem und dunklem Design (KON-080). Hat der Benutzer noch nie gewählt, folgt das Design dem Gerät, auch wenn sich dessen
 * Einstellung ändert. `variant="row"` ist die beschriftete Zeile für das Menü auf dem Handy, `variant="icon"` der Knopf in der Kopfleiste.
 */
export function ThemeToggle({ variant }: { variant: 'icon' | 'row' }) {
  const [theme, setTheme] = useState<Theme>(read);

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (stored()) return;
      const next = query.matches ? 'dark' : 'light';
      apply(next, false);
      setTheme(next);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const dark = theme === 'dark';
  const choose = () => {
    const next: Theme = dark ? 'light' : 'dark';
    apply(next, true);
    setTheme(next);
  };
  const label = dark ? 'Zum hellen Design wechseln' : 'Zum dunklen Design wechseln';
  const icon = dark ? (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );

  if (variant === 'icon') {
    return (
      <button type="button" className="topbar__icon theme-toggle" aria-label={label} title={label} onClick={choose}>
        {icon}
      </button>
    );
  }
  return (
    <button type="button" className="theme-row" onClick={choose}>
      {icon}
      <span>{dark ? 'Helles Design' : 'Dunkles Design'}</span>
    </button>
  );
}
